import { observer } from "mobx-react-lite";
import { clientDetailStore } from "@/stores/ClientDetailStore";
import { formatChange } from "@shared-utils/helpers";
import {
  LineAreaChart,
  BarChart,
  DonutChart,
} from "../../ContractorDetailPage/Charts";
import type { StatsChart } from "@/stores/ContractorDetailStore";

function monthlyToChart(monthly: number[], maxMonth: number): StatsChart[] {
  return Array.from({ length: maxMonth }, (_, i) => ({
    month: i + 1,
    value: monthly[i] ?? 0,
  }));
}

function fmtMoney(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} млн ₽`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)} тыс ₽`;
  return `${Math.round(v).toLocaleString("ru-RU")} ₽`;
}

function changeCls(pct: number): string {
  return pct >= 0
    ? "cdp-metric-card__change--positive"
    : "cdp-metric-card__change--negative";
}

const ClientMetrics: React.FC = observer(() => {
  const { stats, isStatsLoading, statsYear } = clientDetailStore;
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // Для текущего года — показываем только прошедшие месяцы (включая текущий).
  // Для прошлых лет — показываем все 12 месяцев.
  const maxMonth = statsYear < currentYear ? 12 : currentMonth;

  if (isStatsLoading && !stats) {
    return (
      <div className="cdp-metrics-wrap" data-testid="cdp-metrics">
        <div className="cdp-metrics-grid cdp-metrics-grid--loading">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="cdp-metric-card cdp-metric-card--skeleton"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const bookingsChart = monthlyToChart(stats.bookings.monthly ?? [], maxMonth);
  const spentChart = monthlyToChart(stats.spent.monthly ?? [], maxMonth);
  const commissionChart = monthlyToChart(
    stats.commission.monthly ?? [],
    maxMonth,
  );

  const appCount = stats.appBookingsPercent.appCount ?? 0;
  const adminCount = stats.appBookingsPercent.adminCount ?? 0;
  const appPct = stats.appBookingsPercent.current ?? 0;

  const bookingSegments = [
    { value: appCount, color: "#0185f7", label: `Приложение — ${appCount}` },
    {
      value: adminCount,
      color: "#dbe8f7",
      label: `Админка — ${adminCount}`,
    },
  ];

  return (
    <div className="cdp-metrics-wrap" data-testid="cdp-metrics">
      <div className="cdp-metrics-grid">
        {/* ── Ров 1: Бронирования + Donut ── */}
        <div className="cdp-metrics-row cdp-metrics-row--top">
          {/* ── Бронирования ── */}
          <div className="cdp-metric-card cdp-metric-card--chart">
            <div className="cdp-metric-card__top">
              <span className="cdp-metric-card__label">Бронирования</span>
              <div className="cdp-metric-card__vals">
                <span className="cdp-metric-card__value">
                  {stats.bookings.current.toLocaleString("ru-RU")}
                </span>
                <span
                  className={`cdp-metric-card__change ${changeCls(stats.bookings.change_percent)}`}
                >
                  {formatChange(stats.bookings.change_percent)}
                </span>
              </div>
            </div>
            <div className="cdp-metric-card__viz">
              <LineAreaChart
                style={{ height: 120 }}
                data={bookingsChart}
                color="#0185f7"
                currentMonth={maxMonth}
                formatValue={(v) => `${Math.round(v)}`}
                testId="bookings-chart"
              />
            </div>
          </div>

          {/* ── % заявок через приложение ── */}
          <div className="cdp-metric-card cdp-metric-card--donut cdp-metric-card--center cdp-metric-card--bookings">
            <div className="cdp-metric-card__top">
              <span className="cdp-metric-card__label">
                Заявки через приложение
              </span>
              <div className="cdp-metric-card__vals">
                <span className="cdp-metric-card__value">
                  {Math.round(appPct)}%
                </span>
              </div>
            </div>
            <div className="cdp-metric-card__viz cdp-metric-card__viz--col">
              <DonutChart
                segments={bookingSegments}
                totalLabel={`${Math.round(appPct)}%`}
                totalSublabel="из приложения"
                size={120}
                strokeWidth={19}
                testId="app-bookings-chart"
              />
              <div className="cdp-metric-legend cdp-metric-legend--col">
                {bookingSegments.map((seg) => (
                  <div key={seg.label} className="cdp-metric-legend__item">
                    <span
                      className="cdp-metric-legend__dot"
                      style={{ background: seg.color }}
                    />
                    <span className="cdp-metric-legend__label">
                      {seg.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* end cdp-metrics-row--top */}

        {/* ── Ров 2: Потрачено + Комиссия ── */}
        <div className="cdp-metrics-row cdp-metrics-row--bottom">
          {/* ── Потрачено ── */}
          <div className="cdp-metric-card cdp-metric-card--chart">
            <div className="cdp-metric-card__top">
              <span className="cdp-metric-card__label">Потрачено</span>
              <div className="cdp-metric-card__vals">
                <span className="cdp-metric-card__value">
                  {fmtMoney(stats.spent.current)}
                </span>
                <span
                  className={`cdp-metric-card__change ${changeCls(stats.spent.change_percent)}`}
                >
                  {formatChange(stats.spent.change_percent)}
                </span>
              </div>
            </div>
            <div className="cdp-metric-card__viz cdp-metric-card__viz--chart-box">
              <BarChart
                data={spentChart}
                color="#0185f7"
                inactiveColor="#bfdbfe"
                currentMonth={maxMonth}
                formatValue={fmtMoney}
                height={110}
                barThickness={14}
                testId="spent-chart"
              />
            </div>
          </div>

          {/* ── Комиссия ── */}
          <div className="cdp-metric-card cdp-metric-card--chart cdp-metric-card--commission">
            <div className="cdp-metric-card__top">
              <span className="cdp-metric-card__label">Оставлено комиссии</span>
              <div className="cdp-metric-card__vals">
                <span className="cdp-metric-card__value">
                  {fmtMoney(stats.commission.current)}
                </span>
                <span
                  className={`cdp-metric-card__change ${changeCls(stats.commission.change_percent)}`}
                >
                  {formatChange(stats.commission.change_percent)}
                </span>
              </div>
            </div>
            <div className="cdp-metric-card__viz cdp-metric-card__viz--chart-box">
              <BarChart
                data={commissionChart}
                color="#10b981"
                inactiveColor="#bbf7d0"
                currentMonth={maxMonth}
                formatValue={fmtMoney}
                height={110}
                barThickness={12}
                testId="commission-chart"
              />
            </div>
          </div>
        </div>
        {/* end cdp-metrics-row--bottom */}
      </div>
    </div>
  );
});

ClientMetrics.displayName = "ClientMetrics";
export default ClientMetrics;
